/**
 * THE PROGRAMS LIST'S RULES — what a card SAYS, decided here and drawn by `routes/Programs.tsx`.
 *
 * `docs/PROGRAMS-DESIGN.md` §4: *"the Workouts library's card list, same tokens: name · `N weeks`
 * · `Assigned to K` · last edited"*.
 *
 * 🔴 SEPARATE FROM THE SCREEN FOR THE REASON `results.ts` IS: a rule in a component can only be
 * proven by rendering it, and three of the four things a card says are COUNTS over rows that
 * arrive from two other tables. A count is exactly the kind of claim that is right in the happy
 * case and wrong on the empty one.
 *
 * ⚠️ AND THE WEEK COUNT IS DERIVED, NEVER STORED (§2.2). `max(week_index)` — so a block whose
 * middle week is empty (a rest week, which is a real thing a coach plans) still reads `4 weeks`
 * rather than `3`, and a block with no days at all reads `0 weeks` rather than `1`.
 */

/** One `coach_program_days` row, reduced to what the list needs. */
export type ProgramDayCount = { programId: string; weekIndex: number };

/** One `coach_program_assignments` row, reduced to what the list needs. */
export type ProgramAssignmentCount = { programId: string; clientId: string };

export type ProgramRow = {
  id: string;
  name: string;
  description: string | null;
  updatedAtISO: string;
};

export type ProgramCard = {
  id: string;
  name: string;
  description: string | null;
  updatedAtISO: string;
  weeks: number;
  /** DISTINCT clients, never rows — see `assignedTo`. */
  assignedTo: number;
};

/**
 * `max(week_index)`, or 0 when the block holds nothing yet.
 *
 * ⚠️ NOT `days.length`, AND NOT THE NUMBER OF DISTINCT WEEK INDEXES. A four-week block whose
 * week 3 is a rest week has day rows in weeks 1, 2 and 4 — three distinct indexes, seven or more
 * rows, and FOUR weeks. Only the maximum answers the question the pager asks.
 */
export function weekCount(days: readonly ProgramDayCount[], programId: string): number {
  let max = 0;
  for (const d of days) if (d.programId === programId && d.weekIndex > max) max = d.weekIndex;
  return max;
}

/**
 * How many CLIENTS a program was given to.
 *
 * ⚠️ DISTINCT CLIENTS, NOT ROWS, and the difference is a real one rather than defensive. The
 * table's unique key is `(program_id, client_id, start_date)` — §2.3 — precisely so that giving
 * the same block to the same client from a LATER Monday is a second row and a legitimate one.
 * Counting rows would tell a coach a block is on three people when it is on one, three times.
 */
export function assignedTo(
  assignments: readonly ProgramAssignmentCount[],
  programId: string,
): number {
  const seen = new Set<string>();
  for (const a of assignments) if (a.programId === programId) seen.add(a.clientId);
  return seen.size;
}

/** The four things a card says, for every program, in the order given. */
export function programCards(
  programs: readonly ProgramRow[],
  days: readonly ProgramDayCount[],
  assignments: readonly ProgramAssignmentCount[],
): ProgramCard[] {
  return programs.map((p) => ({
    ...p,
    weeks: weekCount(days, p.id),
    assignedTo: assignedTo(assignments, p.id),
  }));
}

/**
 * The card's second line.
 *
 * ⚠️ A BLOCK NOBODY IS ON SAYS SO RATHER THAN SAYING NOTHING. An omitted `Assigned to` is the
 * empty-collection-standing-in-for-an-answer shape: a coach cannot tell "nobody yet" from "this
 * card does not show that". And `0 weeks` is a real state — a program created and not yet
 * planned — so it is printed too.
 */
export function programSummary(card: Pick<ProgramCard, "weeks" | "assignedTo">): string {
  const weeks = `${String(card.weeks)} ${card.weeks === 1 ? "week" : "weeks"}`;
  const on = card.assignedTo === 0
    ? "not assigned yet"
    : `assigned to ${String(card.assignedTo)} ${card.assignedTo === 1 ? "client" : "clients"}`;
  return `${weeks} · ${on}`;
}

/**
 * Filter by name, the way the Workouts search does — and ONLY by name, so no row matches on
 * something a coach cannot see it match on.
 */
export function matchesProgramQuery(card: Pick<ProgramCard, "name">, query: string): boolean {
  const q = query.trim().toLowerCase();
  return q === "" || card.name.toLowerCase().includes(q);
}
